import { diffLines, type ChangeObject } from 'diff';
import { markdownHasImage, splitMarkdown } from '../utils/chat-ops-parse';
import { replaceSlots, restoreSlots } from '../utils/markdown-utils';
export interface MarkdownDiffResult {
  added: string[];
  removed: string[];
  unchanged: string[];
  changes: Array<{
    type: 'added' | 'removed' | 'unchanged';
    value: string;
    lineNumber: number;
    block?: boolean;
  }>;
}

/**
 * 比较两个markdown文件的差异
 * @param oldMarkdown 原始markdown内容
 * @param newMarkdown 新的markdown内容
 * @returns 差异比较结果
 */

export function diffMarkdown(
  oldMarkdown: string,
  newMarkdown: string
): MarkdownDiffResult {
  // 提取并替换代码块和表格为slot
  const { slots: oldSlots, markdown: oldWithSlots } = replaceSlots(oldMarkdown);
  const { slots: newSlots, markdown: newWithSlots } = replaceSlots(newMarkdown);
  // 使用JSDiff进行行级别的差异比较
  let diffResult = diffLines(oldWithSlots, newWithSlots, {
    ignoreWhitespace: false, // 不忽略空白
    newlineIsToken: false, // 关键修复：不将换行符作为独立token
  });

  const result: MarkdownDiffResult = {
    added: [],
    removed: [],
    unchanged: [],
    changes: [],
  };

  // 将 diffResult 中携带图片的新增拆开，拆成多个 diffResult

  // 遍历 diffResult 还原slot内容
  diffResult = restoreSlots(diffResult, new Map([...newSlots, ...oldSlots]));

  const newDiffResult: ChangeObject<string>[] = [];

  diffResult.forEach((part: any, partIndex: number) => {
    if (part.added) {
      const { before, image, after } = splitMarkdown(part.value);

      if (before.trim().length > 0) {
        newDiffResult.push({
          ...part,
          value: before,
        });
      }

      if (image.trim().length > 0) {
        newDiffResult.push({
          ...part,
          value: image.trim(),
        });
      }

      if (after.trim().length > 0) {
        newDiffResult.push({
          ...part,
          value: after.trim(),
        });
      }
    } else {
      newDiffResult.push(part);
    }
  });

  console.log('######--newDiffResult', diffResult, newDiffResult);
  // 分别追踪原文和新文的行号
  let oldLineNumber = 1;
  newDiffResult.forEach((part: any, partIndex: number) => {
    // 按行分割，正确处理换行符
    const lines = part.value.split('\n');

    // 移除最后一个空字符串（由split产生的尾部空字符串）
    let actualLines = lines;
    if (lines.length > 1 && lines[lines.length - 1] === '') {
      actualLines = lines.slice(0, -1);
    }

    if (part.added) {
      result.changes.push({
        type: 'added',
        value: part.value,
        lineNumber: oldLineNumber,
      });
    } else {
      actualLines.forEach((line: string, lineIndex: number) => {
        if (part.added) {
          //   result.added.push(line);
          //   result.changes.push({
          //     type: 'added',
          //     value: line,
          //     lineNumber: oldLineNumber + offsetIndex,
          //   });
          //   offsetIndex++;
          //   newLineNumber++;
        } else if (part.removed) {
          result.removed.push(line);
          result.changes.push({
            type: 'removed',
            value: line,
            lineNumber: oldLineNumber,
            // 标记表示当前的部分是否属于一个代码块或者表格
            block: part.block,
          });
          oldLineNumber++;
        } else {
          // unchanged 行，两个行号都递增
          result.unchanged.push(line);
          result.changes.push({
            type: 'unchanged',
            value: line,
            lineNumber: oldLineNumber,
          });
          oldLineNumber++;
        }
      });
    }
  });

  // 移除 unchanged 的行
  result.changes = result.changes.filter(item => item.type !== 'unchanged');

  return result;
}

/**
 * 生成格式化的差异报告
 * @param diffResult 差异比较结果
 * @returns 格式化的差异报告字符串
 */
export function formatMarkdownDiff(diffResult: MarkdownDiffResult): string {
  let report = '## Markdown Diff Report\n\n';

  if (diffResult.added.length > 0) {
    report += '### Added Lines:\n';
    diffResult.added.forEach((line, index) => {
      report += `+ ${line}\n`;
    });
    report += '\n';
  }

  if (diffResult.removed.length > 0) {
    report += '### Removed Lines:\n';
    diffResult.removed.forEach((line, index) => {
      report += `- ${line}\n`;
    });
    report += '\n';
  }

  report += `### Summary:\n`;
  report += `- Added: ${diffResult.added.length} lines\n`;
  report += `- Removed: ${diffResult.removed.length} lines\n`;
  report += `- Unchanged: ${diffResult.unchanged.length} lines\n`;

  return report;
}

/**
 * 比较两个markdown文件并返回详细的差异信息
 * @param oldMarkdown 原始markdown内容
 * @param newMarkdown 新的markdown内容
 * @param includeFormatting 是否包含格式化的报告
 * @returns 完整的差异信息
 */
export function compareMarkdownFiles(
  oldMarkdown: string,
  newMarkdown: string,
  includeFormatting: boolean = false
) {
  const diffResult = diffMarkdown(oldMarkdown, newMarkdown);

  const result = {
    ...diffResult,
    hasChanges: diffResult.added.length > 0 || diffResult.removed.length > 0,
    totalChanges: diffResult.added.length + diffResult.removed.length,
  };

  if (includeFormatting) {
    return {
      ...result,
      formattedReport: formatMarkdownDiff(diffResult),
    };
  }

  return result;
}

type ModifyOperation = {
  type: 'add' | 'remove' | 'modify';
  oldValue: string;
  value: string;
  startLineNumber: number;
  endLineNumber: number;
};

export function replaceMarkdown(
  markdown: string,
  operations: ModifyOperation
): string {
  const start = operations.startLineNumber;
  const end = operations.endLineNumber;
  const lines = markdown.split('\n');
  const newLines = operations.value.split('\n');
  lines.splice(start - 1, end - start + 1, ...newLines);
  return lines.join('\n');
}

// 插入新内容
export function insertMarkdown(
  markdown: string,
  operations: ModifyOperation
): string {
  // 在指定位置插入新内容
  const lines = markdown.split('\n');
  const newLines = operations.value.split('\n');
  const start = Math.max(operations.startLineNumber, 0);

  // 插入新内容
  lines.splice(start, 0, ...newLines);

  return lines.join('\n');
}

export enum AI_OPS {
  INSERT = 'insert',
  DELETE = 'delete',
  REPLACE = 'replace',
}

type Suggestion = {
  op: AI_OPS.INSERT | AI_OPS.DELETE | AI_OPS.REPLACE;
  timestamp: number;
  selection_str: string; // 插入时表示插入在 selection_str 末尾
  start_line_number: number;
  end_line_number: number;
  new_str: string;
};

export function transformerDiffToSuggestion(
  changes: MarkdownDiffResult['changes']
): Suggestion[] {
  const suggestions: Suggestion[] = [];

  if (!changes || changes.length === 0) {
    return suggestions;
  }

  // 过滤掉 unchanged 类型，只处理 added 和 removed
  const filteredChanges = changes.filter(change => change.type !== 'unchanged');

  if (filteredChanges.length === 0) {
    return suggestions;
  }

  // 按行号排序
  const sortedChanges = [...filteredChanges].sort(
    (a, b) => a.lineNumber - b.lineNumber
  );

  // 将连续的相同类型操作分组
  const groups: Array<{
    type: 'added' | 'removed';
    lines: string[];
    startLineNumber: number;
    endLineNumber: number;
  }> = [];

  let currentGroup: {
    type: 'added' | 'removed';
    lines: string[];
    startLineNumber: number;
    endLineNumber: number;
  } | null = null;

  for (let change of sortedChanges) {
    if (!currentGroup) {
      // 开始新的分组
      currentGroup = {
        type: change.type as 'added' | 'removed',
        lines: [change.value],
        startLineNumber: change.lineNumber,
        endLineNumber: change.lineNumber,
      };
    } else if (
      change.type === currentGroup.type &&
      change.lineNumber === currentGroup.endLineNumber + 1
    ) {
      // 先检查  added  情况下的  image
      const hasImage = markdownHasImage(change.value);

      if (change.type === 'added' && hasImage) {
        groups.push(currentGroup);
        currentGroup = {
          type: change.type as 'added' | 'removed',
          lines: [change.value],
          startLineNumber: change.lineNumber,
          endLineNumber: change.lineNumber,
        };
      } else {
        // 连续的同类型行，添加到当前分组
        currentGroup.lines.push(change.value);
        currentGroup.endLineNumber = change.lineNumber;
      }
    } else {
      // 类型不同或行号不连续，保存当前分组并开始新分组
      groups.push(currentGroup);
      currentGroup = {
        type: change.type as 'added' | 'removed',
        lines: [change.value],
        startLineNumber: change.lineNumber,
        endLineNumber: change.lineNumber,
      };
    }
  }

  // 添加最后一个分组
  if (currentGroup) {
    groups.push(currentGroup);
  }

  // 处理分组，检测 replace 操作
  for (let i = 0; i < groups.length; ++i) {
    const currentGroup = groups[i];
    const nextGroup = groups[i + 1];

    if (
      currentGroup.type === 'removed' &&
      nextGroup &&
      nextGroup.type === 'added'
    ) {
      // 检查是否是相邻的 replace 操作
      const isAdjacent =
        nextGroup.startLineNumber === currentGroup.startLineNumber ||
        nextGroup.startLineNumber === currentGroup.startLineNumber + 1;

      if (isAdjacent) {
        // 这是一个 replace 操作
        suggestions.push({
          op: AI_OPS.REPLACE,
          timestamp: Date.now(),
          selection_str: currentGroup.lines.join('\n'),
          start_line_number: currentGroup.startLineNumber,
          end_line_number: currentGroup.endLineNumber,
          new_str: nextGroup.lines.join('\n'),
        });
        // 跳过下一个分组，因为已经合并了
        ++i;
      } else {
        // 不是相邻的，分别处理为 delete 和 insert
        suggestions.push({
          op: AI_OPS.DELETE,
          timestamp: Date.now(),
          selection_str: currentGroup.lines.join('\n'),
          start_line_number: currentGroup.startLineNumber,
          end_line_number: currentGroup.endLineNumber,
          new_str: '',
        });
      }
    } else if (currentGroup.type === 'removed') {
      // 单独的 delete 操作
      suggestions.push({
        op: AI_OPS.DELETE,
        timestamp: Date.now(),
        selection_str: currentGroup.lines.join('\n'),
        start_line_number: currentGroup.startLineNumber,
        end_line_number: currentGroup.endLineNumber,
        new_str: '',
      });
    } else if (currentGroup.type === 'added') {
      // 检查前面是否有相邻的 removed 组（这种情况不应该发生，因为已经在上面处理了）

      suggestions.push({
        op: AI_OPS.INSERT,
        timestamp: Date.now(),
        selection_str: '', // 插入时表示插入在 selection_str 末尾
        start_line_number: currentGroup.startLineNumber - 1,
        end_line_number: currentGroup.startLineNumber - 1,
        new_str: currentGroup.lines.join('\n'),
      });
    }
  }

  return suggestions;
}
